import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api-client';
import { Loader2, FileText, ArrowRight } from 'lucide-react';

interface CreateFormStepProps {
  onComplete: (formId: string, formName: string) => void;
  onSkip: () => void;
}

export function CreateFormStep({ onComplete, onSkip }: CreateFormStepProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a name for your form',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = (await api.createForm({ name: name.trim(), description: description.trim() })) as {
        id: string;
      };
      toast({
        title: 'Form created!',
        description: 'Your form is ready to receive submissions',
      });
      onComplete(result.id, name.trim());
    } catch (error) {
      console.error('Failed to create form:', error);
      toast({
        title: 'Error',
        description: 'Failed to create form. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mt-8">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle className="text-2xl">Create Your First Form</CardTitle>
        <CardDescription>
          A form is a webhook endpoint that receives submissions and forwards them to your connected
          channels (Telegram, Email).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Form Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Contact Form, Feedback Form"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
            <p className="text-xs text-gray-500">
              Choose a name that helps you identify this form later
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="What is this form for?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
              rows={3}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="submit" className="flex-1" disabled={isLoading || !name.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  Create Form
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <Button type="button" variant="ghost" onClick={onSkip} disabled={isLoading}>
              Skip for now
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
